/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { updateLoadingState } from 'store/app.slice';
import { BaseHandler } from './base.handler';
import TireProfileAPI from 'api/tire-pressure';
import NotificationAPI from 'api/notification.api';
import {
  setLimits,
  setAxleWiseLimits,
  setDeviceTireData,
  setFleetData,
  setIsLoadingFleet,
  setIsLoadingVehicle,
  setIsPollingVehicle,
  setIsLoadingDashboard,
  setDashboard,
  suspendNotification,
  setDashboardNotifications,
  setDashboardLeakageNotifications,
  setBatteryData,
} from 'store/tireProfile.slice';

export default class TireProfileHandler extends BaseHandler {
  private readonly api: TireProfileAPI;
  private readonly notificationApi: NotificationAPI;
  constructor() {
    super();

    this.api = new TireProfileAPI();
    this.notificationApi = new NotificationAPI();
  }

  /**
   * Gets Fleet Data
   */
  async getFleetTireProfile() {
    this.dispatch(setIsLoadingFleet(true));
    try {
      const fleetTires = await this.api.getAssetsTires();
      const data = await this.api.getFleetData();
      this.dispatch(setFleetData({ ...data, assetTires: fleetTires }));

      // Fetch battery data for all assets that have devices
      try {
        const devices = (fleetTires || [])
          .filter((t: any) => t?.device?.deviceId && t?.assetType)
          .map((t: any) => ({
            deviceId: t.device.deviceId as string,
            assetType: t.assetType as string,
          }));
        if (devices.length > 0) {
          const batteryMap = await this.api.getBatteryData(devices);
          this.dispatch(setBatteryData(batteryMap || {}));
        }
      } catch (batteryErr) {
        // Non-fatal; proceed without battery info
        // console.warn('Battery data fetch failed', batteryErr);
      }
      this.handleSuccess();
      this.dispatch(setIsLoadingFleet(false));
    } catch (error: any) {
      this.dispatch(setIsLoadingFleet(false));
      let msg: string = '';

      // TODO: Look into other errors that can be thrown
      if (error.isAxiosError) {
        msg = error.response.data.message as string;
      }

      this.handleError(
        'An error occured while retrieving the fleet tire profile data: ' + msg
      );
    }
  }

  /**
   * Fetch battery data for the provided devices
   */
  async getBatteryDataForDevices(
    devices: Array<{ deviceId: string; assetType: string }>
  ) {
    try {
      if (!devices || devices.length === 0) return;
      const batteryMap = await this.api.getBatteryData(devices);
      this.dispatch(setBatteryData(batteryMap || {}));
    } catch (error) {
      // do not surface as blocking error
    }
  }

  /**
   * Gets profile limits
   */
  async get() {
    this.dispatch(updateLoadingState(true));
    try {
      const profiles = await this.api.getTireProfileLimits();
      this.dispatch(setLimits(profiles));
      this.handleSuccess();
    } catch (error: any) {
      let msg: string = '';

      // TODO: Look into other errors that can be thrown
      if (error.isAxiosError) {
        msg = error.response.data.message as string;
      }

      this.handleError(
        'An error occured while retrieving the tire profile limits: ' + msg
      );
    }
  }

  /**
   * Sets profile limits
   */
  async setTireProfileLimits(payload: any) {
    this.dispatch(updateLoadingState(true));
    try {
      const profiles = await this.api.setTireProfileLimits(payload);
      this.dispatch(setLimits(profiles));
      this.handleSuccess();
    } catch (error: any) {
      let msg: string = '';

      // TODO: Look into other errors that can be thrown
      if (error.isAxiosError) {
        msg = error.response.data.message as string;
      }

      this.handleError(
        'An error occured while setting the tire profile limits: ' + msg
      );
    }
  }

  /**
   * Gets Axle Wise Limits
   */
  async getAxleWiseLimits(customerId?: string, assetId?: string) {
    this.dispatch(updateLoadingState(true));
    try {
      const response = await this.api.getAxleWiseLimits(customerId, assetId);
      // Persist to Redux so components can read axle-wise limits from the store
      if (response && Array.isArray(response)) {
        this.dispatch(setAxleWiseLimits(response));
      } else if (response) {
        // API may return a single object or an array — normalise to array
        this.dispatch(setAxleWiseLimits([response]));
      }
      this.handleSuccess();
      return response;
    } catch (error: any) {
      this.dispatch(updateLoadingState(false));
      let msg: string = '';
      if (error.isAxiosError) {
        msg = error.response?.data?.message as string;
      }
      this.handleError(
        'An error occurred while retrieving axle-wise limits: ' + msg
      );
    }
  }

  /**
   * Sets Axle Wise Limits
   */
  async setAxleWiseLimits(payload: any) {
    this.dispatch(updateLoadingState(true));
    try {
      await this.api.setAxleWiseLimits(payload);
      this.handleSuccess();
      return true;
    } catch (error: any) {
      this.dispatch(updateLoadingState(false));
      let msg: string = '';
      if (error.isAxiosError) {
        msg = error.response?.data?.message as string;
      }
      this.handleError(
        'An error occurred while setting axle-wise limits: ' + msg
      );
      return false;
    }
  }

  /**
   * Gets Asset Tire Data
   */
  async getAssetTireData(
    deviceId: string,
    assetType: string,
    startDate: string,
    endDate: string,
    assetId: string,
    make: string,
    model: string,
    options: { silent?: boolean } = {}
  ) {
    if (options.silent) {
      this.dispatch(setIsPollingVehicle(true));
    } else {
      this.dispatch(setIsLoadingVehicle(true));
    }
    try {
      const tireData = await this.api.getAssetTireData(
        deviceId,
        assetType,
        startDate,
        endDate,
        make,
        model
      );

      let tireFitmentData = [];
      try {
        tireFitmentData = await this.api.getAssetTires(assetId);
      } catch (fitmentError: any) {
        console.error('Failed to get fitment data');
      }

      const fitmentHistory = await this.api.getTireHistory(
        assetId,
        startDate,
        endDate
      );

      this.dispatch(
        setDeviceTireData({
          device: deviceId,
          data: {
            ...tireData,
            tireFitmentData,
            fitmentHistory,
          },
        })
      );

      this.handleSuccess();
    } catch (error: any) {
      let msg: string = '';

      if (error.isAxiosError) {
        msg = error?.response?.data?.message as string;
      }

      this.handleError(
        'An error occurred while retrieving the asset tire data: ' + msg
      );
    } finally {
      if (options.silent) {
        this.dispatch(setIsPollingVehicle(false));
      } else {
        this.dispatch(setIsLoadingVehicle(false));
      }
    }
  }

  /**
   * Gets Dashboard Data
   */
  async getDashboard() {
    this.dispatch(setIsLoadingDashboard(true));
    try {
      const data = await this.api.getDashboardData();
      this.dispatch(setDashboard(data));
      this.dispatch(setIsLoadingDashboard(false));
    } catch (error: any) {
      this.dispatch(setIsLoadingDashboard(false));
      let msg: string = '';

      // TODO: Look into other errors that can be thrown
      if (error.isAxiosError) {
        msg = error.response.data.message as string;
      }

      this.handleError(
        'An error occurred while retrieving the tpms dashboard data: ' + msg
      );
    }
  }

  async suspendTpmSNotification(
    notificationId: string,
    isActive: boolean,
    ruleName: string,
    bumperNumber: string,
    note: string,
    source: 'notification' | 'leakage' = 'notification'
  ) {
    this.dispatch(setIsLoadingDashboard(true));
    try {
      await this.notificationApi.updateNotificationStatus(
        notificationId,
        isActive,
        note,
        source
      );
      this.dispatch(
        suspendNotification({
          notificationId,
          source,
        })
      );

      // Show Toast message
      this.handleSuccess(
        `Notification "${ruleName}" for Bumper ID "${bumperNumber}" has been ${
          isActive ? 'UNSOLVED' : 'SOLVED'
        }.`
      );

      this.dispatch(setIsLoadingDashboard(false));
    } catch (error: any) {
      this.dispatch(setIsLoadingDashboard(false));
      let msg: string = '';
      if (error.isAxiosError) {
        msg = error.response.data.message as string;
      }

      this.handleError(
        'An error occurred while updating notification data: ' + msg
      );
    }
  }

  /**
   * Gets Notifications Data
   */
  async getNotifications() {
    try {
      const data = await this.api.getNotificationsData();
      if (data && Array.isArray(data?.notifications)) {
        this.dispatch(setDashboardNotifications(data?.notifications || []));
      } else {
        throw new Error('Invalid data found');
      }
      // Handle tpmsLeakageNotifications from the API response
      if (data && Array.isArray(data?.tpmsLeakageNotifications)) {
        this.dispatch(
          setDashboardLeakageNotifications(data?.tpmsLeakageNotifications || [])
        );
      }
    } catch (error: any) {
      let msg: string = '';
      // TODO: Look into other errors that can be thrown
      if (error.isAxiosError) {
        msg = error.response.data.message as string;
      }

      this.handleError(
        'An error occurred while retrieving the tpms dashboard data: ' + msg
      );
    }
  }

  /**
   * Gets Tire Profile Notifications for a specific customer
   */
  async getTireProfileNotifications(customerId: string): Promise<any> {
    try {
      const data = await this.api.getNotificationsData(customerId);
      return data?.tpmsLeakageNotifications || [];
    } catch (error: any) {
      let msg: string = '';
      if (error.isAxiosError) {
        msg = error.response.data.message as string;
      }

      this.handleError(
        'An error occurred while retrieving tire profile notifications: ' + msg
      );
      return [];
    }
  }

  /**
   * Sets Tire Fitment Date
   */
  async setTireFitment(
    payload: any,
    assetId: string,
    tireCode: string,
    deviceId: string
  ) {
    this.dispatch(updateLoadingState(true));
    try {
      const fitmentData = await this.api.updateTireFitmentDate(
        payload,
        assetId,
        tireCode
      );
      this.handleSuccess();
      return fitmentData;
    } catch (error: any) {
      let msg: string = '';
      if (error.isAxiosError) {
        msg = error.response.data.message as string;
      }

      this.handleError(
        'An error happened while setting the tire fitment: ' + msg
      );
    }
  }

  /**
   * Update Tire Positions
   */
  async updateTirePosition(payload: any, assetId: string) {
    this.dispatch(updateLoadingState(true));
    try {
      await this.api.updateAssetTirePositions({ assetTires: payload }, assetId);
      this.handleSuccess();
      return true;
    } catch (error: any) {
      let msg: string = '';
      if (error.isAxiosError) {
        msg = error.response.data.message as string;
      }

      this.handleError(
        'An error happened while setting the tire fitment: ' + msg
      );
    }
  }

  /**
   * Download report
   */
  async downloadReport(
    startDate: string,
    endDate: string,
    customerCode: string
  ) {
    try {
      const response = await this.api.downloadReport(
        startDate,
        endDate,
        customerCode
      );

      return response;
    } catch (error: any) {
      let msg: string = '';
      if (error.isAxiosError) {
        msg = error.response.data.message as string;
      }
      const errMsg = 'An error happened while downloading report: ' + msg;
      this.handleError(errMsg);
      throw new Error(errMsg);
    }
  }
}
